import { StrictMode, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import ShuttleCore from '@pages/ShuttleCore';
import SsoRedirect from '@pages/auth/SsoRedirect';
import SsoLogin from '@pages/auth/SsoLogin';
import DocusignReturn from '@pages/docusign/DocusignReturn';
import TrackingService from '@services/TrackingService';
// import DemoTool from '@components/steps/demo/DemoTool';
import PageLayout from '@components/guaranteed-income-direct/PageLayout';

import '@pages/auth/axiosExtensions';

const ExampleCore = lazy(() => import('./examples/ExampleCore'));
const AlertExample = lazy(() => import('./examples/AlertExample'));
const BoardExample = lazy(() => import('./examples/BoardExample'));
const ChartExample = lazy(() => import('./examples/ChartExample'));
const CarouselExample = lazy(() => import('./examples/CarouselExample'));
const CarouselExample2 = lazy(() => import('./examples/CarouselExample2'));
const InputExample = lazy(() => import('./examples/InputExample'));
const AddressExample = lazy(() => import('./examples/AddressExample'));
const ModalExample = lazy(() => import('./examples/ModalExample'));
const OffcanvasExample = lazy(() => import('./examples/OffcanvasExample'));
const DropdownExample = lazy(() => import('./examples/DropdownExample'));
const CardExample = lazy(() => import('./examples/CardExample'));
const IncomeChartByAgeExample = lazy(() => import('./examples/IncomeChartByAgeExample'));
const AccordionExample = lazy(() => import('./examples/AccordionExample'));
const JPMAccordionExample = lazy(() => import('./examples/jpm/AccordionExample'));
const MetlifeAccordionExample = lazy(() => import('./examples/metlife-iia/AccordionExample'));
const PaymentOverviewChartExample = lazy(() => import('./examples/jpm/PaymentOverviewChartExample'));

// eslint-disable-next-line prefer-destructuring
const IS_DEV = process.env.IS_DEV;
const ERROR_CHUNK_LOAD = 'ChunkLoadError'; // webpack error

TrackingService.init();

function AppRoutes() {
  let devRoutes;

  // Catches internet connection issues
  window.addEventListener('error', (e) => {
    if (e.error.name === ERROR_CHUNK_LOAD) {
      window.location.reload();
    }
  });

  if (IS_DEV) {
    devRoutes = (
      <>
        <Route path="/examples/alert" element={<ExampleCore><AlertExample /></ExampleCore>} />;
        <Route path="/examples/board" element={<ExampleCore><BoardExample /></ExampleCore>} />;
        <Route path="/examples/carousel" element={<ExampleCore><CarouselExample /></ExampleCore>} />;
        <Route path="/examples/carousel2" element={<ExampleCore><CarouselExample2 /></ExampleCore>} />;
        <Route path="/examples/chart" element={<ExampleCore><ChartExample /></ExampleCore>} />;
        <Route path="/examples/input" element={<ExampleCore><InputExample /></ExampleCore>} />;
        <Route path="/examples/address" element={<ExampleCore><AddressExample /></ExampleCore>} />;
        <Route path="/examples/modal" element={<ExampleCore><ModalExample /></ExampleCore>} />;
        <Route path="/examples/offcanvas" element={<ExampleCore><OffcanvasExample /></ExampleCore>} />;
        <Route path="/examples/dropdown" element={<ExampleCore><DropdownExample /></ExampleCore>} />;
        <Route path="/examples/income-chart" element={<ExampleCore><IncomeChartByAgeExample /></ExampleCore>} />;
        <Route path="/examples/card" element={<ExampleCore><CardExample /></ExampleCore>} />;
        <Route path="/examples/accordion" element={<ExampleCore><AccordionExample /></ExampleCore>} />;
        <Route path="/examples/metlife/accordion" element={<ExampleCore><MetlifeAccordionExample /></ExampleCore>} />;
        <Route path="/examples/jpm/accordion" element={<ExampleCore><JPMAccordionExample /></ExampleCore>} />;
        <Route path="/examples/jpm/payment-overview-chart" element={<ExampleCore><PaymentOverviewChartExample /></ExampleCore>} />;
      </>
    );
  }

  return (
    <Routes>
      {devRoutes}
      <Route
        path="/:shuttleRootPath/auth/sso/redirect"
        element={<div className="global"><SsoRedirect /></div>}
      />
      <Route
        path="/:shuttleRootPath/auth/sso/login"
        element={<div className="global"><SsoLogin /></div>}
      />
      <Route
        path="/:shuttleRootPath/election/:electionId"
        element={<ShuttleCore />}
      /> {/* deprecated */}
      <Route
        path="/:shuttleRootPath/:electionId"
        element={<ShuttleCore />}
      />
      <Route
        path="/:shuttleRootPath/:electionId/page/:stepPath"
        element={<ShuttleCore />}
      />
      <Route
        path="/:shuttleRootPath/plan/:planId/page/:stepPath"
        element={<ShuttleCore />}
      />
      <Route
        path="/:shuttleRootPath/plan/:planId/election/:electionId"
        element={<ShuttleCore />}
      />
      <Route
        path="/:shuttleRootPath/plan/:planId/election/:electionId/page/:stepPath"
        element={<ShuttleCore />}
      />
      <Route
        path="/:shuttleRootPath/plan/:planId"
        element={<ShuttleCore />}
      />
      <Route
        path="/:shuttleRootPath/page/:stepPath"
        element={<ShuttleCore />}
      />
      <Route
        path="/:shuttleRootPath/"
        element={<ShuttleCore />}
      />
      <Route
        path="/"
        element={<ShuttleCore />}
      />
      <Route
        path="/docusign-return"
        element={<DocusignReturn />}
      />
      <Route
        path="/loading"
        element={<DocusignReturn />} // TODO: deprecated route, remove
      />
      <Route
        path="*"
        element={(
          <PageLayout
            pageTitle="Page Not Found"
            pageName="404"
          >404 | Page not found!
          </PageLayout>
)}
      />
    </Routes>
  );
}

const root = createRoot(document.getElementById('root'));
root.render(
  <StrictMode>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </StrictMode>,
);

// Copyright © 2026 Micruity Inc.
